import type { FabricObject } from 'fabric';

/**
 * Smart-object tool framework.
 *
 * A tool is a RECIPE: parameters in → a Group of 100% standard fabric
 * primitives out. The recipe id + params are stored on the group's `data`
 * property, so:
 *   - any fabric app (including the official tool) loads the template as
 *     plain JSON with zero custom code — it just sees an editable group;
 *   - editors that register the recipes can "re-open" a smart object,
 *     tweak params, and regenerate it in place.
 *
 * HARD RULE: generate() may only emit stock fabric types (Group, Textbox,
 * FabricText, Rect, Circle, Ellipse, Path, Polygon, Polyline, Line, Image).
 * Never subclass — a custom `type` in the JSON breaks import elsewhere.
 */

/** Serialized onto the group as `data` — must survive toObject round-trips. */
export interface SmartObjectData {
  tool: string;
  version: number;
  params: Record<string, unknown>;
  /**
   * Editor behavior: proportional-scaling lock. Smart objects are
   * aspect-locked BY DEFAULT (absent/true = locked — editors hide the
   * side-stretch handles); false only when a user explicitly unlocks.
   */
  aspectLocked?: boolean;
  /**
   * Grade-band styling memory (Smart Tools modal, 2026-07-23): the last
   * GradeBandId preset the educator one-clicked for this object, so reopening
   * the edit modal lights the same chip. Pure UI memory — params already
   * carry the merged values; recipes never read it.
   */
  gradeBand?: string;
}

/**
 * A fill value for 'fill'-type params. A plain string (solid color) stays
 * valid everywhere — richer fills are tagged objects. resolveFill() (fill.ts)
 * turns any of these into what fabric accepts.
 */
export type FillValue =
  | string
  | { type: 'solid'; color: string }
  | {
      type: 'linear-gradient';
      /** Legacy two-color form — still valid everywhere. */
      from?: string;
      to?: string;
      /** Rich form: 2+ stops (offset 0..1). Wins over from/to when present. */
      stops?: { offset: number; color: string }[];
      angle?: number;
    }
  | { type: 'radial-gradient'; stops: { offset: number; color: string }[] }
  | { type: 'texture'; url: string; scale?: number };

/** One field of a recipe's parameter schema — drives the editor's param panel. */
export interface ParamField {
  key: string;
  label: string;
  /**
   * Editor-facing control type. Educator-first rules (owner, 2026-07-13):
   * 'list' replaces raw 'json' for arrays (dynamic rows with +/− and a detail
   * modal per row); 'fill' is a rich fill picker (solid / gradient / texture);
   * 'json' remains ONLY as a last-resort escape hatch and should not appear in
   * new schemas.
   */
  type:
    | 'text' | 'multiline' | 'number' | 'color' | 'select' | 'font' | 'image'
    | 'boolean' | 'json' | 'list' | 'fill';
  /**
   * For type 'select'. An option MAY carry `group`: consecutive options that
   * share a `group` render inside an <optgroup> of that label (educator-first —
   * a 119-option element picker becomes Period 1…7 sections; a trailing
   * ungrouped option like "Custom" stays a bare <option>). Grouping is by
   * CONTIGUOUS run, so keep same-group options adjacent (they already are when
   * the list is built in order). Ungrouped selects render exactly as before.
   */
  options?: { value: string; label: string; group?: string }[];
  min?: number;
  max?: number;
  step?: number;
  /** Short hint rendered under the field. */
  help?: string;
  /**
   * Progressive disclosure: advanced fields render collapsed under an
   * "Advanced" section. The visible face of every tool stays simple.
   */
  advanced?: boolean;
  /**
   * Visual grouping in the editor's param panel (e.g. 'Content',
   * 'Typography', 'Colors', 'Layout'). Ungrouped fields render first.
   */
  section?: string;
  /** For type 'list': the schema of ONE item (rendered in the row modal). */
  itemSchema?: ParamField[];
  /**
   * For type 'list': item keys shown in the collapsed row summary (first
   * value doubles as the row title). Defaults to the first itemSchema key.
   */
  summaryKeys?: string[];
  /** For type 'list': bounds for the +/− controls. */
  minItems?: number;
  maxItems?: number;
  /**
   * For type 'list' when items are scalars (e.g. a palette of colors): the
   * field type of each item; itemSchema is ignored.
   */
  itemType?: 'text' | 'number' | 'color';
  /**
   * Conditional visibility (SM-F2, 2026-07-23). Render this field ONLY when the
   * ONE referenced sibling satisfies the condition, evaluated against the
   * CURRENT draft values — top-level fields resolve against the modal draft,
   * itemSchema sub-fields against the row's own values. Exactly one condition,
   * no boolean algebra:
   *   { key, equals }     → visible when values[key] equals the value
   *   { key, notEquals }  → visible when values[key] does NOT equal the value
   * Comparison is value-equality with number/boolean-string tolerance (a '2'
   * draft matches equals:2, a 'true' draft matches equals:true). A hidden field
   * KEEPS its draft value — never cleared — so flipping the controller back
   * reveals the prior input untouched. Absent → always visible. Enforced purely
   * in the UI renderers (SmartToolModal.jsx, SmartObjectListField.jsx); recipes
   * never read it, and generate() always receives every param (hidden or not),
   * so the visibility rule never changes recipe output.
   */
  visibleWhen?: {
    key: string;
    equals?: string | number | boolean;
    notEquals?: string | number | boolean;
  };
}

export type ToolCategory = 'text' | 'chart' | 'math' | 'decor' | 'practice' | 'callouts' | 'science';

/**
 * Grade-band ids for one-click styling presets (Smart Tools modal). DESIGN
 * NOTE: when the Theme system lands, a band becomes a theme-variant selector —
 * the modal's control keeps its (bandId: string) => void API so that swap is
 * invisible to every tool.
 */
export type GradeBandId = 'K2' | 'G35' | 'G68' | 'G912';

/**
 * OPTIONAL per-tool modal layout (Smart Tools modal, 2026-07-23). Layout is
 * DATA, never per-tool React — the one SmartToolModal component reads this so
 * hundreds of tools stay cheap. Every field is optional; a recipe without a
 * `modal` spec renders perfectly well from its schema alone (hero none,
 * 1 column, sections in schema order, advanced folded).
 */
export interface ToolModalSpec {
  /** Param keys pinned prominent (larger controls) at the top of the form. */
  hero?: string[];
  /** Field columns inside each section card (default 1). */
  columns?: 1 | 2;
  /** Section cards in this order; unlisted sections follow in schema order. */
  sectionOrder?: string[];
  /** Hide the Advanced accordion entirely (values still apply from defaults/params). */
  hideAdvanced?: boolean;
}

/* ======================================================================
 * SMART CONTENT SOURCE (Smart Tools epic · SM-H, 2026-07-23) — a tool can
 * FILL ITSELF from a curriculum standard, drawing on the IIG knowledge graph
 * (be_printables worker: GET /api/standards/:id/content). Educator-first laws:
 *
 *   • GRAPH PROPOSES, TEACHER DISPOSES. A fill lands ONLY in the editor
 *     modal's DRAFT — the educator reviews and edits every item, then Inserts.
 *     applyContent NEVER touches the canvas; it returns a new params patch.
 *   • ids-never-URLs: the payload carries opaque graph ids, not links; saved
 *     params stay plain JSON.  • no student PII ever crosses this wire.
 *   • EXPORTABILITY: the network is used at AUTHORING time only. A recipe's
 *     generate() must render from params alone — it never reads a ContentSource.
 * ==================================================================== */

/** A curriculum standard the educator picked to fill from (ids, never URLs). */
export interface StandardRef { id: string; code: string; description: string; state?: string; subject?: string; grade?: string; }

/**
 * The graph's PROPOSAL for one standard — concept/skill/vocabulary/
 * misconception/example/non-example rows lifted from the IIG knowledge graph.
 * Every list may be empty (nothing linked); each item carries its graph id so
 * a recipe can cite ids-never-URLs. A recipe's `applyContent` shapes whichever
 * slices it `wants` into its own params.
 */
export interface StandardContentPayload {
  standard: StandardRef;
  concepts: Array<{ id: string; name: string; definition?: string }>;
  skills: Array<{ id: string; name: string }>;
  vocabulary: Array<{ id: string; term: string; definition?: string; example?: string }>;
  misconceptions: Array<{ id: string; text: string; correction?: string }>;
  examples: Array<{ id: string; text: string }>;
  nonExamples: Array<{ id: string; text: string }>;
  /**
   * Provenance (SM-J). true ⇒ served content is AI-drafted and NOT yet
   * teacher-reviewed (graph status ∉ reviewed/verified) — the modal flags it so
   * the educator checks it before printing (graph proposes / teacher disposes).
   * false ⇒ all served content is reviewed/verified library content.
   */
  generated?: boolean;
  /**
   * JIT-enrichment signal (SM-J), present only when the standard has no servable
   * content. 'queued' ⇒ the product's enrichment queue is drafting this
   * standard's content now (poll again shortly and it fills in automatically);
   * 'unavailable' ⇒ nothing ready and none coming (feature off, or no concept to
   * enrich). Absent ⇒ content was served.
   */
  enrichment?: 'queued' | 'unavailable';
  /**
   * Where the served content came from (SM-K). 'graph' ⇒ the knowledge-graph
   * library. 'instant-draft' ⇒ a synchronous cheap-model draft made just now
   * because the graph was empty (always generated:true; the product's judged
   * enrichment replaces it in the graph after the fact — owner: "quick fast and
   * cheap … right away; submit to the graph after the fact").
   */
  source?: 'graph' | 'instant-draft';
}

/**
 * Declares that a recipe can fill from a standard, and HOW. The modal renders
 * the "Fill from a standard" block only for recipes carrying this; `wants`
 * hints which slices matter (the fetch always returns all — `wants` is for the
 * UI). `applyContent` is PURE: given the graph payload and the CURRENT draft
 * params, it returns the params to MERGE into the draft (draft-only law — the
 * educator still reviews before Insert/Apply). It must not mutate `current`.
 */
export interface ContentSourceSpec {
  kind: 'standard';
  wants: Array<'concepts'|'skills'|'vocabulary'|'misconceptions'|'examples'|'nonExamples'>;
  applyContent: (payload: StandardContentPayload, current: Record<string, unknown>) => Record<string, unknown>;
}

export interface ToolRecipe {
  /** Stable kebab-case id, e.g. "art-text". Never rename — templates reference it. */
  id: string;
  name: string;
  category: ToolCategory;
  /** Bump when generate() output changes shape for the same params. */
  version: number;
  description: string;
  defaults: Record<string, unknown>;
  schema: ParamField[];
  /**
   * Insert-time behavior: editors that force-fit new objects to a preview
   * size (e.g. scale to ~35% of the workspace) must NOT do so when this is
   * true — the recipe's geometry is a PHYSICAL promise (params in inches ×
   * the 72-units law), and rescaling on insert would void it (an answer box
   * asked for at 3×2 in must land at exactly 216×144 units). Recipe-level
   * because the recipe is what makes the promise; per-object scaling after
   * insert remains the user's own choice.
   */
  insertAtTrueScale?: boolean;
  /** Optional editor-modal layout hints — see ToolModalSpec. */
  modal?: ToolModalSpec;
  /**
   * One-click grade-band styling: a PARTIAL params patch per band, merged
   * into the editor's draft when the educator clicks [K-2] [3-5] [6-8]
   * [9-12]. Values must be honest members of the recipe's own schema (font
   * sizes/weights, palette chunkiness, spacing) — never new keys. Bands are
   * optional per recipe AND per band id.
   */
  gradeBands?: Partial<Record<GradeBandId, Record<string, unknown>>>;
  /* ---- SMART TOOLS BROWSER taxonomy (SM-B, 2026-07-23) ------------------
   * Three OPTIONAL, additive fields powering the Smart Tools Browser (the
   * 220-tool catalog surface). Back-compat: `category` above stays the
   * compatibility field; a recipe that omits `domain` is mapped onto one by
   * the browser via CATEGORY_TO_DOMAIN (see studioCore/smartToolsTaxonomy). */
  /**
   * Instructional-domain id — the recipe's HONEST home in the K-12 taxonomy
   * (studioCore/smartToolsTaxonomy DOMAIN_LABELS): math | ela | science |
   * social | sel | display | sped | cte | languages | arts | observances |
   * ops (+ future ids). MAY differ from the legacy `category` when the
   * category was a shipping convenience (e.g. handwriting-lines is
   * category:'math' but domain:'ela'; timeline is category:'math' but
   * domain:'social'). Absent → the browser derives it from `category`.
   */
  domain?: string;
  /**
   * Freeform discovery tags — kebab or plain words ('fractions', 'data',
   * 'K-2', 'anchor-chart', 'labels', 'answer-key', …). The browser matches
   * them as case-insensitive substrings (name + description + tags) and
   * renders them as clickable filter chips on each tile.
   */
  tags?: string[];
  /**
   * Surfaces in the browser's default "Featured" view AND the slimmed Smart
   * drawer's featured grid. Curate sparingly — flagship / highest-use tools
   * only (the drawer shows at most 6).
   */
  featured?: boolean;
  /**
   * OPTIONAL smart-content source (SM-H, 2026-07-23). When present, the editor
   * modal offers a "Fill from a standard" block that pulls IIG graph content
   * and runs `applyContent` to MERGE a params patch into the modal DRAFT — the
   * educator reviews/edits, then Inserts (graph proposes, teacher disposes).
   * Absent → the tool is filled manually, exactly as before. Authoring-time
   * only: generate() never reads this, so exportability is untouched.
   */
  contentSource?: ContentSourceSpec;
  /**
   * Build the object. Runs in the browser (editor / render harness) — fabric
   * image/pattern loading is allowed. Must return a Group (or single object)
   * positioned at left/top 0,0; the caller places it.
   */
  generate(params: Record<string, unknown>): Promise<FabricObject>;
}

/** Attach smart-object metadata (call at the end of every generate()). */
export function tagSmartObject<T extends FabricObject>(
  obj: T,
  recipe: Pick<ToolRecipe, 'id' | 'version'>,
  params: Record<string, unknown>,
): T {
  const data: SmartObjectData = { tool: recipe.id, version: recipe.version, params };
  obj.set('data', data);
  return obj;
}

/** True when a loaded object carries re-editable smart-object metadata. */
export function smartObjectData(obj: FabricObject): SmartObjectData | undefined {
  const data = (obj as { data?: unknown }).data;
  if (
    data &&
    typeof data === 'object' &&
    typeof (data as SmartObjectData).tool === 'string' &&
    typeof (data as SmartObjectData).params === 'object'
  ) {
    return data as SmartObjectData;
  }
  return undefined;
}
